// ============================================================
//  src/admin/components/ConfirmModal.js
//  Promise-based confirm dialog. Wrap the admin app once with
//  <ConfirmProvider>, then anywhere inside call:
//
//    const confirm = useConfirm();
//    const ok = await confirm({
//      title: 'Block this user?',
//      message: 'They will be logged out and unable to sign in.',
//      confirmText: 'Block User',
//      tone: 'danger', // 'danger' | 'primary'
//    });
//    if (ok) doAction('block');
// ============================================================
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      setState({
        title: opts.title || 'Are you sure?',
        message: opts.message || '',
        confirmText: opts.confirmText || 'Confirm',
        cancelText: opts.cancelText || 'Cancel',
        tone: opts.tone || 'primary',
        resolve,
      });
    });
  }, []);

  const handle = useCallback((result) => {
    setState(s => {
      if (s) s.resolve(result);
      return null;
    });
  }, []);

  useEffect(() => {
    if (!state) return;
    function onKey(e) {
      if (e.key === 'Escape') handle(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, handle]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div className="bf-modal-overlay" onClick={() => handle(false)}>
          <div className="bf-modal" onClick={e => e.stopPropagation()} role="alertdialog" aria-modal="true" aria-labelledby="bf-modal-title">
            <div className={`bf-modal-icon bf-modal-icon-${state.tone === 'danger' ? 'danger' : 'primary'}`}>
              {state.tone === 'danger' ? '⚠️' : '❓'}
            </div>
            <div id="bf-modal-title" className="bf-modal-title">{state.title}</div>
            {state.message && <div className="bf-modal-message">{state.message}</div>}
            <div className="bf-modal-actions">
              <button className="btn btn-outline" onClick={() => handle(false)}>{state.cancelText}</button>
              <button
                autoFocus
                className={state.tone === 'danger' ? 'bf-btn-danger' : 'btn btn-primary'}
                onClick={() => handle(true)}
              >
                {state.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider');
  return ctx;
}